import { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDebounce } from '@/hooks/useDebounce';

interface SearchableMultiSelectProps {
  options: Array<{ id: number; name: string | { ar: string; en: string } }>;
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noOptionsText?: string;
  maxVisible?: number;
  disabled?: boolean;
  className?: string;
}

export const SearchableMultiSelect = ({
  options,
  selectedIds,
  onChange,
  placeholder,
  searchPlaceholder,
  noOptionsText,
  maxVisible = 5,
  disabled = false,
  className = '',
}: SearchableMultiSelectProps) => {
  const { t, language, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const optionName = typeof option.name === 'string' 
      ? option.name 
      : (language === 'ar' ? option.name.ar : option.name.en);
    
    return optionName.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
  });

  // Get selected options for display
  const selectedOptions = options.filter(option => selectedIds.includes(option.id));

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            toggleOption(filteredOptions[highlightedIndex].id);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions]);

  const toggleOption = (optionId: number) => {
    if (selectedIds.includes(optionId)) {
      onChange(selectedIds.filter(id => id !== optionId));
    } else {
      onChange([...selectedIds, optionId]);
    }
  };

  const removeSelected = (optionId: number) => {
    onChange(selectedIds.filter(id => id !== optionId));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getOptionName = (option: typeof options[0]) => {
    return typeof option.name === 'string' 
      ? option.name 
      : (language === 'ar' ? option.name.ar : option.name.en);
  };

  const displaySelected = selectedOptions.slice(0, maxVisible);
  const remainingCount = selectedOptions.length - maxVisible;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected items display */}
      <div className="min-h-[42px] p-2 border rounded-lg bg-background">
        {selectedOptions.length === 0 ? (
          <div 
            className={`text-muted-foreground cursor-text ${isRTL ? 'text-right' : 'text-left'}`}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            {placeholder || t('common.selectOptions') || 'Select options...'}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1 items-center">
            {displaySelected.map(option => (
              <Badge 
                key={option.id} 
                variant="secondary" 
                className="flex items-center gap-1 px-2 py-1"
              >
                <span className="text-xs">{getOptionName(option)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-3 w-3 p-0 hover:bg-transparent ml-1"
                  onClick={() => removeSelected(option.id)}
                  disabled={disabled}
                >
                  <X className="h-2 w-2" />
                </Button>
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Badge variant="outline" className="px-2 py-1">
                +{remainingCount}
              </Badge>
            )}
            {selectedOptions.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs hover:bg-transparent ml-2"
                onClick={clearAll}
                disabled={disabled}
              >
                {t('common.clearAll') || 'Clear all'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Search input and dropdown */}
      <div className="relative mt-1">
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={searchPlaceholder || (t('common.search') || 'Search...')}
          disabled={disabled}
          className={`pr-20 ${isRTL ? 'pr-10 pl-10' : 'pl-10'}`}
        />
        <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
        
        {/* Dropdown arrow */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`absolute top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 ${isRTL ? 'left-1' : 'right-1'}`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
        >
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-background border rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-center text-muted-foreground">
              {noOptionsText || (t('common.noOptionsFound') || 'No options found')}
            </div>
          ) : (
            <div className="py-1">
              {filteredOptions.map((option, index) => {
                const isSelected = selectedIds.includes(option.id);
                const isHighlighted = index === highlightedIndex;
                
                return (
                  <div
                    key={option.id}
                    className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary/10 text-primary' : ''
                    } ${
                      isHighlighted ? 'bg-muted' : ''
                    } hover:bg-muted`}
                    onClick={() => toggleOption(option.id)}
                  >
                    <div className={`flex items-center flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <span className="text-sm">{getOptionName(option)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
